import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import { useRxCollectionInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider/provider";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import { randomUniqueId } from "$routes/_lib/random-id";
import { invoiceNumberFromId } from "$routes/billing/_lib/invoice-number";
import { BillingCurrency, PatientType, PayerType } from "elephant-web/gql";
import { useCallback } from "react";
import { useIntl } from "react-intl";

const BILLING_CURRENCIES = new Set<string>(Object.values(BillingCurrency));
const isBillingCurrency = (v: string | undefined): v is BillingCurrency =>
  !!v && BILLING_CURRENCIES.has(v);

export const useCreateInvoiceRxdb = (patientUuid: string) => {
  const collection = useRxCollectionInvoice();
  const user = useUser();
  const { code: currencyCode } = useBillingCurrency();
  const { getCurrentTime } = useServerTime();
  const { formatMessage } = useIntl();

  const createInvoice = useCallback(async (): Promise<{
    id: string;
  } | null> => {
    try {
      if (!collection) {
        throw new Error("Invoice collection is not available");
      }
      if (!isBillingCurrency(currencyCode)) {
        throw new Error(`Unsupported currency: ${currencyCode ?? "unset"}`);
      }

      const now = getCurrentTime().toISOString();
      const newInvoiceId = randomUniqueId();

      const invoice = Invoice.from({
        id: newInvoiceId,
        facilityId: user.facilityId,
        patientId: patientUuid,
        patientType: PatientType.Outpatient,
        invoiceNumber: invoiceNumberFromId(newInvoiceId),
        currency: currencyCode,
        phase: "DRAFT",
        inpatientDetails: null,
        lineItems: [],
        payers: [
          {
            id: randomUniqueId(),
            name: "Patient",
            type: PayerType.Patient,
            insuranceDetails: null,
          },
        ],
        transactions: [],
        finalizedAt: null,
        finalizedByUserId: null,
        priceListLastSyncedAt: null,
        meta: {
          createdAt: now,
          updatedAt: now,
          createdByUserId: user.id,
          updatedByUserId: user.id,
        },
      }).toRecord();

      await collection.insert(invoice);

      return { id: newInvoiceId };
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          formatMessage({
            id: "invoicing.createInvoice.error.msg",
            defaultMessage: "Failed to create a new invoice",
          }),
        );
      }
      return null;
    }
  }, [
    collection,
    currencyCode,
    formatMessage,
    getCurrentTime,
    patientUuid,
    user.facilityId,
    user.id,
  ]);

  return { createInvoice };
};
